import { supabase } from "@/integrations/supabase/client";

export const PROPERTY_IMAGE_BUCKET = "property-images";

export const HOUSE_TYPES = [
  "Detached House",
  "Detached Bungalow",
  "Semi-Detached House",
  "Semi-Detached Bungalow",
  "Mid-Terraced House",
  "Annex",
] as const;

export type HouseType = (typeof HOUSE_TYPES)[number];

export type PhotoStage = "before" | "during" | "after";

export interface RoofArray {
  orientation_deg: number;
  pitch_deg: number;
  shading_factor: number;
}

export interface PropertyImage {
  id: string;
  reference_code: string;
  stage: PhotoStage;
  storage_path: string;
  sort_order: number;
  /** Signed URL resolved at fetch time. */
  url: string | null;
}

export interface Property {
  reference_code: string;
  house_type: HouseType;
  install_date: string;
  system_size_kwp: number;
  inverter_model: string;
  has_battery: boolean;
  /** Placeholder flag - rename once the source column is confirmed. */
  has_secondary_feature: boolean;
  roof_arrays: RoofArray[];
  annual_yield_kwh: number;
  self_sufficiency_pct: number | null;
  installer_name: string | null;
  images: PropertyImage[];
}

const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

/** Sortable numeric key from a display string like "Dec 2023 (est.)". */
export function installSortKey(installDate: string): number {
  const year = Number(installDate.match(/\b(19|20)\d{2}\b/)?.[0] ?? 0);
  const monthToken = installDate.slice(0, 3).toLowerCase();
  const month = MONTHS.indexOf(monthToken);
  return year * 12 + (month >= 0 ? month : 0);
}

export function installYear(installDate: string): string | null {
  return installDate.match(/\b(19|20)\d{2}\b/)?.[0] ?? null;
}

/**
 * Plain-language compass label. Orientation is measured from due south,
 * negative to the east, positive to the west.
 */
export function compassLabel(orientationDeg: number): string {
  let d = ((orientationDeg + 180) % 360 + 360) % 360 - 180;
  const east = d < 0;
  d = Math.abs(d);
  if (d < 22.5) return "South";
  if (d < 67.5) return east ? "South East" : "South West";
  if (d < 112.5) return east ? "East" : "West";
  if (d < 157.5) return east ? "North East" : "North West";
  return "North";
}

export function coverImage(images: PropertyImage[]): PropertyImage | null {
  const byStage = (stage: PhotoStage) => images.find((image) => image.stage === stage) ?? null;
  return byStage("after") ?? byStage("during") ?? byStage("before") ?? null;
}

export function stageImages(images: PropertyImage[], stage: PhotoStage): PropertyImage[] {
  return images
    .filter((image) => image.stage === stage)
    .sort((a, b) => a.sort_order - b.sort_order);
}

export function hasBeforeAndAfter(images: PropertyImage[]): boolean {
  return images.some((i) => i.stage === "before") && images.some((i) => i.stage === "after");
}

/** Max 5 photos per property, ordered before -> during -> after. */
const STAGE_ORDER: Record<PhotoStage, number> = { before: 0, during: 1, after: 2 };

async function signImages(rows: Omit<PropertyImage, "url">[]): Promise<PropertyImage[]> {
  if (rows.length === 0) return [];
  const paths = rows.map((row) => row.storage_path);
  const { data } = await supabase.storage
    .from(PROPERTY_IMAGE_BUCKET)
    .createSignedUrls(paths, 60 * 60);
  const urlByPath = new Map<string, string>();
  data?.forEach((entry) => {
    if (entry.path && entry.signedUrl) urlByPath.set(entry.path, entry.signedUrl);
  });
  return rows.map((row) => ({ ...row, url: urlByPath.get(row.storage_path) ?? null }));
}

function attachImages(
  properties: Omit<Property, "images">[],
  images: PropertyImage[],
): Property[] {
  return properties.map((property) => ({
    ...property,
    roof_arrays: Array.isArray(property.roof_arrays) ? property.roof_arrays : [],
    images: images
      .filter((image) => image.reference_code === property.reference_code)
      .sort(
        (a, b) => STAGE_ORDER[a.stage] - STAGE_ORDER[b.stage] || a.sort_order - b.sort_order,
      )
      .slice(0, 5),
  }));
}

export async function fetchProperties(): Promise<Property[]> {
  const [{ data: properties, error }, { data: imageRows, error: imageError }] = await Promise.all([
    supabase.from("properties").select("*"),
    supabase.from("property_images").select("*"),
  ]);
  if (error) throw error;
  if (imageError) throw imageError;
  const images = await signImages((imageRows ?? []) as unknown as Omit<PropertyImage, "url">[]);
  return attachImages(
    (properties ?? []) as unknown as Omit<Property, "images">[],
    images,
  ).sort((a, b) => installSortKey(b.install_date) - installSortKey(a.install_date));
}

export async function fetchProperty(referenceCode: string): Promise<Property | null> {
  const all = await fetchProperties();
  return (
    all.find(
      (property) => property.reference_code.toLowerCase() === referenceCode.toLowerCase(),
    ) ?? null
  );
}

/* ---------------------------------------------------------------------------
 * Customer-facing display helpers. Derived only from stored data.
 * ------------------------------------------------------------------------ */

export type SystemFilter = "all" | "solar" | "battery";
export type SizeBand = "all" | "under-4" | "4-6" | "6-plus";

export const SIZE_BANDS: { value: SizeBand; label: string }[] = [
  { value: "all", label: "All Sizes" },
  { value: "under-4", label: "Under 4 kWp" },
  { value: "4-6", label: "4 - 6 kWp" },
  { value: "6-plus", label: "6+ kWp" },
];

export function matchesSizeBand(sizeKwp: number, band: SizeBand): boolean {
  switch (band) {
    case "under-4":
      return sizeKwp < 4;
    case "4-6":
      return sizeKwp >= 4 && sizeKwp <= 6;
    case "6-plus":
      return sizeKwp > 6;
    default:
      return true;
  }
}

/** e.g. "5.16 kWp Solar & Battery System". */
export function installationTitle(property: Property): string {
  return `${property.system_size_kwp} kWp Solar ${property.has_battery ? "& Battery " : ""}System`;
}

/** e.g. "A 5.16 kWp Solar & Battery System Designed for This Detached House". */
export function installationHeadline(property: Property): string {
  return `A ${installationTitle(property)} Designed for This ${property.house_type}`;
}

export function systemTypeLabel(property: Property): string {
  return property.has_battery ? "Solar + Battery" : "Solar Only";
}

export function formatKwh(value: number): string {
  return `${value.toLocaleString("en-GB")} kWh`;
}

/** Accessible, non-identifying alt text for an installation photo. */
export function imageAlt(property: Property, stage: PhotoStage): string {
  const subject =
    stage === "before"
      ? "roof before the solar installation"
      : stage === "during"
        ? "solar panels being installed on the roof"
        : "completed solar panel installation on the roof";
  return `${property.house_type}, ${subject}, ${property.system_size_kwp} kWp CRS Energy system`;
}

/** Ranked similar installations using stored attributes only. */
export function similarProperties(property: Property, all: Property[], limit = 3): Property[] {
  return all
    .filter((item) => item.reference_code !== property.reference_code)
    .map((item) => {
      let score = 0;
      if (item.house_type === property.house_type) score += 3;
      if (item.has_battery === property.has_battery) score += 2;
      const delta = Math.abs(item.system_size_kwp - property.system_size_kwp);
      if (delta <= 1) score += 2;
      else if (delta <= 2) score += 1;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

/* ---------------------------------------------------------------------------
 * SEO + case study copy helpers.
 * ------------------------------------------------------------------------ */

/** "Mid-Terraced House" -> "Mid-Terraced Home" so headings read naturally. */
export function homePhrase(houseType: string): string {
  return houseType.replace(/House$/i, "Home");
}

/** e.g. "2.73 kWp Solar & Battery Installation" */
export function installationSeoTitle(property: Property): string {
  return `${property.system_size_kwp} kWp Solar ${property.has_battery ? "& Battery " : ""}Installation`;
}

/** Single H1: describes the installation and the property it was designed for. */
export function installationH1(property: Property): string {
  return `${installationSeoTitle(property)} on a ${homePhrase(property.house_type)}`;
}

/** Unique, non-generic page title built from real installation data. */
export function caseStudyPageTitle(property: Property): string {
  return `${installationSeoTitle(property)} | ${property.house_type} | CRS Energy`;
}

/** Unique meta description using only stored installation data. */
export function caseStudyMetaDescription(property: Property): string {
  const system = property.has_battery ? "solar and battery" : "solar panel";
  const base = `See this real CRS Energy ${property.system_size_kwp} kWp ${system} installation on a ${property.house_type.toLowerCase()}, including the system specification`;
  return property.annual_yield_kwh > 0
    ? `${base} and an estimated ${formatKwh(property.annual_yield_kwh)} generated a year.`
    : `${base} and the photos from the completed job.`;
}

